import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'chakra-react-select';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { HoldingMethod } from '@blockpulse3/data/shared';
import { FiscalAccountType } from '@blockpulse3/graphql/hooks';
import {
  ErrorMessage,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
} from '@blockpulse3/ui/commons';

import { fiscalAccountDefaults, fiscalAccountSchema } from './schema';
import { IFiscalAccountForm } from './types';

const defaultOptions = [
  { label: 'PEA', value: FiscalAccountType.PEA },
  { label: 'PEA-PME', value: FiscalAccountType.PEA_PME },
];

type Props = {
  /* ** Submit handler ** */
  onSubmit: (data: IFiscalAccountForm) => void;
  /* ** Optional parameter used to filter fiscal account options ** */
  allowedHoldingMethods?: HoldingMethod[] | null | undefined;
} & Omit<ResponsiveModalProps, 'children'>;

/**
 * FiscalAccountFormModal.
 * Modal form to create a fiscal account. A modal forces a reset.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function FiscalAccountFormModal({
  onSubmit,
  allowedHoldingMethods,
  ...props
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nFiscalAccountForm = useTranslations('FiscalAccount');

  /* ** Filter select options with allowed holding methods ** */
  const options = !allowedHoldingMethods
    ? defaultOptions
    : defaultOptions.filter(
        (option) => allowedHoldingMethods?.includes(HoldingMethod[option.value]),
      );

  /* ** Overwrite default values with existing option ** */
  const defaultValues = { ...fiscalAccountDefaults, type: options?.[0] };

  const { control, register, formState, handleSubmit, reset } = useForm<IFiscalAccountForm>({
    defaultValues,
    resolver: yupResolver(fiscalAccountSchema),
  });

  const handleFormSubmit: SubmitHandler<IFiscalAccountForm> = (data) => {
    onSubmit(data);
  };

  useEffect(() => {
    /* ** The form is being render in a modal, we need to reset the fields after the submit ** */
    if (formState.isSubmitSuccessful) {
      reset({ ...fiscalAccountDefaults });
    }
  }, [formState.isSubmitSuccessful, reset]);

  return (
    <ResponsiveModal {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nFiscalAccountForm('AddFiscalAccount')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="fiscal-account" onSubmit={handleSubmit(handleFormSubmit)}>
            <Stack spacing="5">
              <Controller
                control={control}
                name="type"
                render={({ field }): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors.type} w={{ base: 'full', md: '50%' }}>
                    <FormLabel htmlFor="assetType">
                      {i18nFiscalAccountForm('FiscalAccountType')}
                    </FormLabel>
                    <Select id="pea-type" menuPlacement="auto" options={options} {...field} />
                  </FormControl>
                )}
              />
              <FormControl isInvalid={!!formState.errors.accountNumber}>
                <FormLabel htmlFor="accountNumber">
                  {i18nFiscalAccountForm('FiscalAccountNumber')}
                </FormLabel>
                <Input id="accountNumber" type="text" {...register('accountNumber')} />
                <ErrorMessage error={formState.errors.accountNumber} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.bankName}>
                <FormLabel htmlFor="bankName">{i18nFiscalAccountForm('BankName')}</FormLabel>
                <Input id="bankName" type="text" {...register('bankName')} />
                <ErrorMessage error={formState.errors?.bankName} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.bankName}>
                <FormLabel htmlFor="bankCode">{i18nFiscalAccountForm('BankCode')}</FormLabel>
                <Input id="bankCode" type="text" {...register('bankCode')} />
                <ErrorMessage error={formState.errors?.bankCode} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.bankEmail}>
                <FormLabel htmlFor="bankerEmail">{i18nFiscalAccountForm('BankEmail')}</FormLabel>
                <Input id="bankerEmail" type="email" {...register('bankEmail')} />
              </FormControl>
            </Stack>
          </form>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button form="fiscal-account" type="submit">
            {i18nGeneral('Submit')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type FiscalAccountFormModalProps = Props;
